// Centralized RBAC constants — mirrors the backend API_REFERENCE exactly.
// Server enforces all permissions; these are purely for UI gating.

const ROLE_ALIASES = {
  company_member: "company_representative",
  doctor: "hospital_doctor",
};

export const ROLE_LABELS = {
  super_admin: "Super Admin",
  hospital_admin: "Hospital Admin",
  hospital_doctor: "Doctor",
  hospital_pa: "PA",
  hospital_base: "Hospital Base",
  company_admin: "Company Admin",
  company_core_lab: "Company Core Lab",
  company_manager: "Company Manager",
  company_representative: "Company Representative",
  hospital_member: "Hospital Base",
  company_member: "Company Representative",
  owner: "Owner",
  admin: "Admin",
  core_lab: "Core Lab",
  radiologist: "Radiologist",
  cardiologist: "Cardiologist",
  hospital: "Hospital",
  distributor: "Distributor",
  lab_technician: "Lab Technician",
  manager: "Manager",
  doctor: "Doctor",
  organisation: "Organisation",
  medical_company: "Medical Company",
  viewer: "Viewer",
};

// Tailwind badge colour classes per role
export const ROLE_COLORS = {
  super_admin: "bg-purple-50 text-purple-700 border-purple-200",
  hospital_admin: "bg-red-50 text-red-700 border-red-200",
  hospital_doctor: "bg-cyan-50 text-cyan-700 border-cyan-200",
  hospital_pa: "bg-green-50 text-green-700 border-green-200",
  hospital_base: "bg-green-50 text-green-700 border-green-200",
  company_admin: "bg-rose-50 text-rose-700 border-rose-200",
  company_core_lab: "bg-blue-50 text-blue-700 border-blue-200",
  company_manager: "bg-indigo-50 text-indigo-700 border-indigo-200",
  company_representative: "bg-orange-50 text-orange-700 border-orange-200",
  hospital_member: "bg-green-50 text-green-700 border-green-200",
  company_member: "bg-orange-50 text-orange-700 border-orange-200",
  owner: "bg-purple-50 text-purple-700 border-purple-200",
  admin: "bg-red-50 text-red-700 border-red-200",
  core_lab: "bg-blue-50 text-blue-700 border-blue-200",
  radiologist: "bg-sky-50 text-sky-700 border-sky-200",
  cardiologist: "bg-violet-50 text-violet-700 border-violet-200",
  hospital: "bg-green-50 text-green-700 border-green-200",
  distributor: "bg-lime-50 text-lime-700 border-lime-200",
  lab_technician: "bg-amber-50 text-amber-700 border-amber-200",
  manager: "bg-indigo-50 text-indigo-700 border-indigo-200",
  doctor: "bg-cyan-50 text-cyan-700 border-cyan-200",
  organisation: "bg-teal-50 text-teal-700 border-teal-200",
  medical_company: "bg-emerald-50 text-emerald-700 border-emerald-200",
  viewer: "bg-slate-100 text-slate-600 border-slate-200",
};

const ADMIN_ROUTES = ["/", "/weekly-dashboard", "/patients", "/organizations", "/upload", "/viewer", "/reports", "/billing", "/users", "/dashboard-cards", "/audit", "/flowchart", "/search", "/studies", "/sharing"];
const HOSPITAL_ADMIN_ROUTES = ["/", "/weekly-dashboard", "/patients", "/organizations", "/upload", "/viewer", "/reports", "/billing", "/users", "/audit", "/search", "/studies", "/sharing"];
const HOSPITAL_DOCTOR_ROUTES = ["/", "/weekly-dashboard", "/patients", "/upload", "/viewer", "/search", "/studies", "/sharing"];
const HOSPITAL_PA_ROUTES = ["/patients", "/upload", "/viewer", "/search", "/studies", "/sharing"];
const HOSPITAL_CLINICAL_ROUTES = ["/patients", "/upload", "/viewer", "/reports", "/search", "/studies", "/sharing"];
const COMPANY_ADMIN_ROUTES = ["/", "/weekly-dashboard", "/patients", "/organizations", "/upload", "/viewer", "/reports", "/billing", "/users", "/audit", "/search", "/studies", "/sharing"];
const COMPANY_CORE_LAB_ROUTES = ["/", "/weekly-dashboard", "/patients", "/upload", "/viewer", "/billing", "/search", "/studies", "/sharing"];
const COMPANY_MANAGER_ROUTES = ["/", "/weekly-dashboard", "/patients", "/upload", "/viewer", "/reports", "/billing", "/search", "/studies", "/sharing"];
const COMPANY_REPRESENTATIVE_ROUTES = ["/", "/weekly-dashboard", "/patients", "/upload", "/viewer", "/reports", "/search", "/studies", "/sharing"];

// Route access per role (source of truth for UI gating)
export const ROLE_ROUTE_ACCESS = {
  super_admin: ADMIN_ROUTES,
  hospital_admin: HOSPITAL_ADMIN_ROUTES,
  hospital_doctor: HOSPITAL_DOCTOR_ROUTES,
  hospital_pa: HOSPITAL_PA_ROUTES,
  company_admin: COMPANY_ADMIN_ROUTES,
  company_core_lab: COMPANY_CORE_LAB_ROUTES,
  company_manager: COMPANY_MANAGER_ROUTES,
  company_representative: COMPANY_REPRESENTATIVE_ROUTES,
  // Legacy aliases
  hospital_base: HOSPITAL_CLINICAL_ROUTES,
  hospital_member: HOSPITAL_CLINICAL_ROUTES,
  company_member: COMPANY_REPRESENTATIVE_ROUTES,
  owner: ADMIN_ROUTES,
  admin: ADMIN_ROUTES,
  core_lab: COMPANY_CORE_LAB_ROUTES,
  radiologist: COMPANY_CORE_LAB_ROUTES,
  cardiologist: COMPANY_CORE_LAB_ROUTES,
  hospital: HOSPITAL_DOCTOR_ROUTES,
  distributor: HOSPITAL_DOCTOR_ROUTES,
  lab_technician: HOSPITAL_CLINICAL_ROUTES,
  manager: COMPANY_MANAGER_ROUTES,
  doctor: HOSPITAL_DOCTOR_ROUTES,
  organisation: ["/billing"],
  medical_company: ["/billing"],
  viewer: ["/patients", "/viewer", "/search", "/studies"],
};

const HOSPITAL_DOCTOR_PERMISSIONS = [
  "view_dashboard", "view_patients", "manage_patients", "upload", "view_imaging",
  "share", "download", "export_csv", "filter_hospital",
];
const HOSPITAL_PA_PERMISSIONS = [
  "view_patients", "upload", "view_imaging", "share",
];
const COMPANY_REP_PERMISSIONS = [
  "view_dashboard", "view_patients", "manage_patients", "upload", "view_imaging",
  "share", "download", "view_reports", "export_csv", "filter_hospital", "upload_on_behalf", "view_territory",
];

// Default permissions per role (for display + permission-based fallback)
export const ROLE_PERMISSIONS = {
  super_admin: [
    "view_dashboard", "view_patients", "manage_patients", "upload", "view_imaging",
    "view_billing", "manage_users", "view_audit", "view_flowchart",
    "download", "share", "manage_reference_data", "view_all_territories", "view_turnaround",
    "view_reports", "export_patient_csv", "export_csv", "filter_hospital", "filter_all_users",
    "approve_deletion",
  ],
  hospital_admin: [
    "view_dashboard", "view_patients", "manage_patients", "upload", "view_imaging",
    "view_billing", "manage_users", "view_audit", "download", "share",
    "view_reports", "export_csv", "filter_hospital", "filter_all_users", "approve_deletion",
  ],

  hospital_doctor: HOSPITAL_DOCTOR_PERMISSIONS,
  hospital_pa: HOSPITAL_PA_PERMISSIONS,
  hospital_base: [
    "view_patients", "manage_patients", "upload", "view_imaging", "share", "download",
  ],
  company_admin: [
    "view_dashboard", "view_patients", "manage_patients", "upload", "view_imaging", "share",
    "view_billing", "manage_users", "view_audit", "upload_on_behalf", "view_territory", "view_team_rollup",
    "download", "view_reports", "export_patient_csv", "export_csv",
    "filter_hospital", "filter_all_users", "approve_deletion",
  ],

  company_core_lab: [
    "view_dashboard", "view_patients", "manage_patients", "upload", "view_imaging", "share",
    "view_billing", "upload_report", "view_all_territories", "view_turnaround",
    "download", "export_csv", "filter_hospital", "filter_all_users",
  ],
  company_manager: [
    "view_dashboard", "view_patients", "manage_patients", "upload", "view_imaging", "share",
    "view_billing", "upload_on_behalf", "view_territory", "view_team_rollup",
    "download", "view_reports", "export_csv", "filter_hospital", "filter_all_users", "approve_deletion",
  ],
  company_representative: COMPANY_REP_PERMISSIONS,
  hospital_member: [
    "view_patients", "manage_patients", "upload", "view_imaging", "share", "download",
  ],
  company_member: COMPANY_REP_PERMISSIONS,
  owner: [
    "view_dashboard", "view_patients", "manage_patients", "upload", "view_imaging",
    "view_billing", "manage_users", "view_audit", "view_flowchart",
    "download", "share", "manage_reference_data", "view_all_territories", "view_turnaround",
    "view_reports", "export_patient_csv", "export_csv", "filter_hospital", "filter_all_users",
    "approve_deletion",
  ],
  admin: [
    "view_dashboard", "view_patients", "manage_patients", "upload", "view_imaging",
    "view_billing", "manage_users", "view_audit", "view_flowchart",
    "download", "share", "manage_reference_data", "view_all_territories", "view_turnaround",
    "view_reports", "export_patient_csv", "export_csv", "filter_hospital", "filter_all_users",
    "approve_deletion",
  ],
  core_lab: [
    "view_dashboard", "view_patients", "upload", "view_imaging", "view_billing",
    "upload_report", "view_all_territories", "view_turnaround", "download", "export_csv",
    "filter_hospital", "filter_all_users",
  ],
  radiologist: [
    "view_dashboard", "view_patients", "upload", "view_imaging", "view_billing",
    "upload_report", "view_all_territories", "view_turnaround", "download", "export_csv",
    "filter_hospital", "filter_all_users",
  ],
  cardiologist: [
    "view_dashboard", "view_patients", "upload", "view_imaging", "view_billing",
    "upload_report", "view_all_territories", "view_turnaround", "download", "export_csv",
    "filter_hospital", "filter_all_users",
  ],
  hospital: HOSPITAL_DOCTOR_PERMISSIONS,
  distributor: HOSPITAL_DOCTOR_PERMISSIONS,
  lab_technician: HOSPITAL_PA_PERMISSIONS,
  manager: [
    "view_dashboard", "view_patients", "upload", "view_imaging", "view_billing",
    "upload_on_behalf", "view_territory", "view_team_rollup", "download", "view_reports", "export_csv",
    "filter_hospital", "filter_all_users", "approve_deletion",
  ],
  doctor: HOSPITAL_DOCTOR_PERMISSIONS,
  organisation: ["view_billing"],
  medical_company: ["view_billing"],
  viewer: ["view_patients", "view_imaging"],
};

// Human-readable description for each role (used in RBAC info cards)
export const ROLE_DESCRIPTIONS = {
  super_admin: "Full system access: dashboard, patients, upload, viewer, reports, billing, user management, audit, and flowchart.",
  hospital_admin: "Hospital admin: dashboard, patients, upload, viewer, reports, billing, user management, and deletion approval.",
  hospital_doctor: "Doctor: dashboard, patients, upload, anonymisation, sharing, and CSV export. No reports list or deletion approval.",
  hospital_pa: "Physician assistant: upload and share on behalf of a doctor. No dashboard, reports, anonymisation, or exports.",
  hospital_base: "Hospital staff: patients, upload, viewer, and reports.",
  company_admin: "Company admin: dashboard, patients, upload, viewer, reports, user management, patient CSV export, and deletion approval.",
  company_core_lab: "Core lab: dashboard, patients, upload, billing, and CSV export. No reports list or deletion approval.",
  company_manager: "Territory manager: dashboard, patients, upload, reports, billing, all-users filter, and team deletion approval. No reports CSV export.",
  company_representative: "Company representative: dashboard, patients, upload, reports, sharing, and CSV export. No all-users filter, reports CSV export, or deletion approval.",
  owner: "Full system owner. All permissions including billing, audit, user and reference data management.",
  admin: "Full system access. Manage users, patients, reference data, billing and audit logs.",
  core_lab: "Core lab workflow: view, download, upload reports across all territories.",
  radiologist: "Radiology specialist: view, download, and upload signed reports (core lab alias).",
  cardiologist: "Cardiology specialist: view, download, and upload signed reports (core lab alias).",
  hospital: "Hospital staff: upload CT studies and view/download their hospital's data.",
  distributor: "Distributor: upload studies and view/download scoped to their hospital.",
  lab_technician: "Lab technician: upload and view imaging data. No download or sharing.",
  manager: "Territory manager: full company_representative access plus team rollup dashboard.",
  doctor: "Doctor: dashboard, patients, upload, anonymisation, sharing, and CSV export.",
  organisation: "Organisation billing contact: billing dashboard only. No clinical data.",
  medical_company: "Medical company (legacy): billing dashboard only (organisation alias).",
  viewer: "Temporary view-only access to shared resources. No upload or download.",
};

// Roles available for assignment when creating users
export const ASSIGNABLE_ROLES = [
  "super_admin",
  "hospital_admin",
  "hospital_doctor",
  "hospital_pa",
  "company_admin",
  "company_core_lab",
  "company_manager",
  "company_representative",
];

// Every unique permission that exists across all roles
export const ALL_PERMISSIONS = [
  "view_dashboard",
  "view_patients",
  "view_imaging",
  "view",
  "upload",
  "download",
  "share",
  "manage_users",
  "manage_patients",
  "manage_reference_data",
  "upload_report",
  "upload_on_behalf",
  "view_all_territories",
  "view_territory",
  "view_team_rollup",
  "view_billing",
  "view_turnaround",
  "view_audit",
  "view_flowchart",
  "view_reports",
  "export_patient_csv",
  "export_csv",
  "filter_hospital",
  "filter_all_users",
  "approve_deletion",
];

// Route-level permission requirement (fallback when role is unknown).
// null = any authenticated user may access.
export const ROUTE_PERMISSIONS = {
  "/": "view_dashboard",
  "/weekly-dashboard": "view_dashboard",
  "/billing": "view_billing",
  "/patients": "view_patients",
  "/organizations": "view_dashboard",
  "/hospitals": "view_dashboard",
  "/studies": "view_imaging",
  "/upload": "upload",
  "/viewer": "view_imaging",
  "/reports": "view_reports",
  "/sharing": null,
  "/search": "view_imaging",
  "/users": "manage_users",
  "/dashboard-cards": "manage_users",
  "/audit": "view_audit",
  "/flowchart": "view_flowchart",
};

const matchesRoute = (allowedPath, path) => {
  if (allowedPath === "/studies" && path.startsWith("/studies/pending-deletions")) {
    return false;
  }
  if (allowedPath === "/reports" && path.startsWith("/reports/")) {
    return false;
  }
  return allowedPath === path || (allowedPath !== "/" && path.startsWith(`${allowedPath}/`));
};

/** Normalize role strings from API / cache (trim, lowercase, aliases). */
export const normalizeRole = (role) => {
  if (!role) return null;
  const normalized = String(role).trim().toLowerCase();
  if (!normalized) return null;
  return ROLE_ALIASES[normalized] ?? normalized;
};

const toRoleSet = (roles) => new Set(roles);

const DASHBOARD_ROLES = toRoleSet([
  "super_admin", "owner", "admin",
  "hospital_admin", "hospital_doctor",
  "company_admin", "company_core_lab", "company_manager", "company_representative",
]);

const HOSPITAL_FILTER_ROLES = toRoleSet([
  "super_admin", "owner", "admin",
  "hospital_admin", "hospital_doctor",
  "company_admin", "company_core_lab", "company_manager", "company_representative",
]);

const ALL_USERS_FILTER_ROLES = toRoleSet([
  "super_admin", "owner", "admin",
  "hospital_admin",
  "company_admin", "company_core_lab", "company_manager",
]);

const EXPORT_PATIENT_CSV_ROLES = toRoleSet([
  "super_admin", "owner", "admin", "company_admin",
]);

const EXPORT_CSV_ROLES = toRoleSet([
  "super_admin", "owner", "admin",
  "hospital_admin", "hospital_doctor",
  "company_admin", "company_core_lab", "company_manager", "company_representative",
]);

const ANONYMIZE_ROLES = toRoleSet([
  "super_admin", "owner", "admin",
  "hospital_admin", "hospital_doctor",
  "company_admin", "company_core_lab", "company_manager", "company_representative",
]);

const STUDY_DELETION_ADMIN_ROLES = toRoleSet([
  "super_admin",
  "hospital_admin",
  "company_admin",
  "company_manager",
  "owner",
  "admin",
]);

const REPORTS_LIST_ROLES = toRoleSet([
  "super_admin",
  "hospital_admin",
  "company_admin",
  "company_manager",
  "company_representative",
  "owner",
  "admin",
  "manager",
]);

/** Roles that may export CSV from the Reports page (excludes company manager/rep). */
const EXPORT_REPORTS_CSV_ROLES = toRoleSet([
  "super_admin",
  "hospital_admin",
  "company_admin",
  "owner",
  "admin",
]);

const roleIn = (role, set) => set.has(normalizeRole(role));

const isIndividualDoctorFlag = (options = {}) =>
  Boolean(options?.individualDoctor || options?.individual_doctor);

/** Admins (and individual doctors) who can approve/reject study deletion requests. */
export const canApproveStudyDeletion = (role, options = {}) => {
  if (isIndividualDoctorFlag(options)) return true;
  return roleIn(role, STUDY_DELETION_ADMIN_ROLES);
};

export const canFilterHospital = (role, options = {}) => {
  if (isIndividualDoctorFlag(options)) return false;
  return roleIn(role, HOSPITAL_FILTER_ROLES);
};

export const canFilterAllUsers = (role) => roleIn(role, ALL_USERS_FILTER_ROLES);

export const canExportPatientCsv = (role) => roleIn(role, EXPORT_PATIENT_CSV_ROLES);

export const canExportCsv = (role) => roleIn(role, EXPORT_CSV_ROLES);

/** Whether a role may export CSV from the Reports list page. */
export const canExportReportsCsv = (role, options = {}) => {
  if (isIndividualDoctorFlag(options)) return true;
  return roleIn(role, EXPORT_REPORTS_CSV_ROLES);
};

export const canAnonymizePatients = (role) => roleIn(role, ANONYMIZE_ROLES);

export const canViewReportsList = (role, options = {}) => {
  if (isIndividualDoctorFlag(options)) return true;
  return roleIn(role, REPORTS_LIST_ROLES);
};

/** Whether a role may access a route path. */
export const canRoleAccessRoute = (role, path) => {
  const key = normalizeRole(role);
  const routes = key ? ROLE_ROUTE_ACCESS[key] : null;
  if (!routes) return null;
  return routes.some((route) => matchesRoute(route, path));
};

export const canAccessDashboard = (permissions = [], role) => {
  const normalizedRole = normalizeRole(role);
  if (normalizedRole && normalizedRole in ROLE_ROUTE_ACCESS) {
    return DASHBOARD_ROLES.has(normalizedRole);
  }
  return (
    permissions.includes("view_dashboard") ||
    permissions.includes("manage_reference_data")
  );
};

export const canAccessBillingDashboard = (permissions = [], role) => {
  const normalizedRole = normalizeRole(role);
  if (normalizedRole && normalizedRole in ROLE_ROUTE_ACCESS) {
    return canRoleAccessRoute(normalizedRole, "/billing");
  }
  return permissions.includes("view_billing");
};

const canViewReportDetail = (permissions = [], role, options = {}) => {
  if (canViewReportsList(role, options)) return true;
  const routeAccess = canRoleAccessRoute(role, "/viewer");
  if (routeAccess != null) return routeAccess;
  return permissions.includes("view_imaging") || permissions.includes("view");
};

/** Whether the user may access a given route path. */
export const canAccessRoute = (permissions = [], path, role, options = {}) => {
  const normalizedRole = normalizeRole(role);
  const pathname = String(path || "").split("?")[0];

  if (pathname.startsWith("/studies/pending-deletions")) {
    return canApproveStudyDeletion(normalizedRole, options);
  }

  if (pathname === "/reports") {
    if (normalizedRole && normalizedRole in ROLE_ROUTE_ACCESS) {
      return canViewReportsList(normalizedRole, options);
    }
    return permissions.includes("view_reports");
  }

  if (pathname.startsWith("/reports/")) {
    return canViewReportDetail(permissions, normalizedRole, options);
  }

  // Role matrix is authoritative when the role is known
  if (normalizedRole && normalizedRole in ROLE_ROUTE_ACCESS) {
    return canRoleAccessRoute(normalizedRole, pathname);
  }

  if (pathname === "/" || pathname === "/weekly-dashboard") {
    return canAccessDashboard(permissions, normalizedRole);
  }
  if (pathname === "/billing") return canAccessBillingDashboard(permissions, normalizedRole);

  const required = ROUTE_PERMISSIONS[pathname];
  if (!required) return true;

  if (permissions.includes(required)) return true;

  // Legacy permission aliases (patients only — "view" must not unlock viewer/reports)
  if (required === "view_dashboard" && permissions.includes("manage_reference_data")) return true;
  if (required === "view_patients" && permissions.includes("view")) return true;

  return false;
};

const FALLBACK_ROUTE_PRIORITY = [
  "/patients",
  "/upload",
  "/viewer",
  "/reports",
  "/billing",
  "/sharing",
  "/search",
  "/users",
  "/audit",
  "/flowchart",
];

/** Default landing page after login. */
export const getDefaultRoute = (permissions = [], role) => {
  if (canAccessDashboard(permissions, role)) return "/";
  if (canAccessBillingDashboard(permissions, role)) return "/billing";

  for (const route of FALLBACK_ROUTE_PRIORITY) {
    if (canAccessRoute(permissions, route, role)) return route;
  }
  return "/sharing";
};

/** Maps UI actions to routes (same rules as sidebar navigation). */
const ACTION_ROUTES = {
  manage_patients: "/patients",
  upload: "/upload",
  share: "/sharing",
  view_imaging: "/viewer",
  view_reports: "/reports",
};

/** Whether a role may perform a UI action. */
export const canRoleAction = (role, action) => {
  const path = ACTION_ROUTES[action];
  if (!path) return null;
  return canAccessRoute([], path, role);
};

/**
 * Role-first action check for buttons (add patient, share study, view study, etc.).
 * Uses the same route matrix as navigation — no separate permission strings.
 */
export const canPerformAction = (permissions = [], action, role) => {
  const path = ACTION_ROUTES[action];
  if (!path) return false;
  return canAccessRoute(permissions, path, role);
};

/** Permissions for a role (UI fallback when API omits them). */
export const permissionsForRole = (role) => ROLE_PERMISSIONS[normalizeRole(role)] ?? ROLE_PERMISSIONS[role] ?? [];

/** Quick helper — does this permissions array include the given permission? */
export const can = (permissions, perm) => Array.isArray(permissions) && permissions.includes(perm);

/**
 * Dev helper — verify every role lands on an accessible route.
 * Returns roles whose default route fails canAccessRoute.
 */
export const findRbacIssues = () => {
  const issues = [];
  for (const role of ASSIGNABLE_ROLES) {
    const permissions = ROLE_PERMISSIONS[role] ?? [];
    const route = getDefaultRoute(permissions, role);
    if (!canAccessRoute(permissions, route, role)) {
      issues.push({ role, route, permissions });
    }
  }
  return issues;
};
